'use client';

/**
 * ShareFallback component has fallback logic for all those scenarios where web share api is not supported. We are using react-share
 * library (https://github.com/nygardk/react-share) to easily share on different social media platforms.
 */

import { useState, type SyntheticEvent } from 'react';
import { Snackbar } from '@lmn/design-system';
import {
  EmailLegacyIcon,
  EmailShareButton,
  FacebookLegacyIcon,
  FacebookShareButton,
  XLegacyIcon,
  XShareButton,
} from 'react-share';
import { IconDetails } from '@/app/common/icons/libraryIcons';
import styled, { css } from 'styled-components';
import { trackSocialShare } from '@/app/components/hotel-detail/trackInteractionEvents';
import { useTranslation } from '@/app/components/translations/Translation';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { useUrlSearchParams } from './UrlSearchParamsProvider';

type ShareFallbackProps = {
  title: string;
} & React.HTMLAttributes<HTMLElement>;

const cssButton = css`
  display: flex;
  align-items: center;
  justify-content: center;

  svg,
  [data-library-icon] {
    width: 32px;
    height: 32px;
  }
`;

const CopyButton = styled.div`
  ${cssButton}
  border-radius: 50%;
  background: ${(p) => p.theme.kallisto.color.background.interactive.backgroundTertiaryDefault};
  color: ${(p) => p.theme.kallisto.color.icon.static.iconOnDark};
  cursor: pointer;
  width: 32px;
  height: 32px;
  padding: 8px;
`;

type AdaptAppSharingUrlParams = {
  accommodationOnly: boolean;
  isApp?: boolean;
};

export function adaptAppSharingUrl({ accommodationOnly, isApp = false }: AdaptAppSharingUrlParams): string {
  const shareUrlAppSubSourceHO = 'S03PDSB0S03PD01';
  const shareUrlAppSubSourceDP = 'S07PDSB0S07PD01';

  if (!canUseDOM) {
    return '';
  }

  if (!isApp) {
    return window.location.href;
  }
  const href = new URL(window.location.href);
  ['app', 'businessProfileId', 'bfSubSource', 'bf_subsource'].forEach((paramName) => {
    if (href.searchParams.get(paramName)) {
      href.searchParams.delete(paramName);
    }
  });
  href.searchParams.set(
    URLParameters.BF_SUB_SOURCE,
    accommodationOnly ? shareUrlAppSubSourceHO : shareUrlAppSubSourceDP
  );
  return href.toString();
}

function copyUrlToClipboard(accommodationOnly: boolean, isApp?: boolean) {
  if (navigator?.clipboard) {
    const url = adaptAppSharingUrl({ accommodationOnly, isApp });
    navigator.clipboard.writeText(url);
  }
}

export default function ShareFallback(props: ShareFallbackProps) {
  const [showUrlCopied, setShowUrlCopied] = useState(false);
  const { t } = useTranslation();
  const {
    commonData: { isApp },
  } = usePageWrapperData();
  const { accommodationOnly } = useUrlSearchParams();
  const { title } = props;

  const shareUrl = adaptAppSharingUrl({ accommodationOnly, isApp });
  const showCopyToClipboard = !!navigator?.clipboard;

  const facebookClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    trackSocialShare('facebook');
  };

  const twitterClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    trackSocialShare('twitter');
  };

  const emailClick = () => {
    trackSocialShare('email');
  };

  const copyClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    copyUrlToClipboard(accommodationOnly, isApp);
    setShowUrlCopied(true);
    trackSocialShare('copy');
  };

  return (
    <>
      {/* The legacy icons keep the round badge look; react-share 6's default icons are bare glyphs. */}
      <FacebookShareButton css={cssButton} url={shareUrl} onClick={facebookClick}>
        <FacebookLegacyIcon size={32} round={true} />
      </FacebookShareButton>

      <XShareButton url={shareUrl} text={title} css={cssButton} onClick={twitterClick}>
        <XLegacyIcon size={32} round={true} />
      </XShareButton>

      <EmailShareButton url={shareUrl} subject={title} css={cssButton} beforeOnClick={emailClick}>
        <EmailLegacyIcon size={32} round={true} />
      </EmailShareButton>

      {showCopyToClipboard && (
        <CopyButton onClick={copyClick}>
          <IconDetails />
        </CopyButton>
      )}
      {showCopyToClipboard && (
        <Snackbar
          open={showUrlCopied}
          autoHideDuration={3000}
          handleClose={() => setShowUrlCopied(false)}
          variant="success"
          message={t('vacation.select.copy.url')}
          position={{ horizontal: 'center' }}
          showCloseButton={false}
        />
      )}
    </>
  );
}
